export type VisualQuality = "stage8" | "high" | "medium" | "low";

export interface Stage8VisualManifest {
  schema: string;
  visualVersion: string;
  quality: VisualQuality;
  delivery?: {
    contentHashRequired?: boolean;
    appShellFallbackForAssets?: boolean;
  };
  assets: {
    publicSite?: {
      hero?: {
        path: string;
        sha256: string;
        status?: string;
      };
    };
    worldBiomes: {
      skinlands: {
        backdrop: {
          path: string;
          sha256: string;
          status?: string;
        };
      };
    };
  } & Record<string, unknown>;
}

export interface VisualManifestState {
  loaded: boolean;
  quality: string;
  visualVersion: string;
  heroPath: string;
  skinlandsGameplayPath: string;
  runtimeGlbCount: number;
  contentHashRequired: boolean;
  appShellFallbackForAssets: boolean;
  error: string;
}

export interface Stage8RuntimeGlbs {
  ashBoundSmith?: string;
  weaponGreatsword?: string;
  remnantChassis?: string;
  remnantHead?: string;
  remnantLimb?: string;
}

export interface Stage8RuntimeVisuals {
  ashBoundSmithCutout?: string;
}

const MANIFEST_PATHS: Record<VisualQuality, string> = {
  stage8: "/assets/manifest.stage8.json",
  high: "/assets/manifest.high.json",
  medium: "/assets/manifest.medium.json",
  low: "/assets/manifest.low.json"
};

export const FALLBACK_HERO_PATH = "/assets/visual/visual_soulsmith_ashbound_hero_v01_58bbe19d.png";
export const FALLBACK_SKINLANDS_GAMEPLAY_PATH = "/assets/visual/visual_skinlands_gameplay_plate_v01_1e4e99e9.png";

const FALLBACK_MANIFEST: Stage8VisualManifest = {
  schema: "soulsmith.stage8.visual-manifest.v1",
  visualVersion: "stage8-visual-revival-2026-06-06",
  quality: "stage8",
  delivery: {
    contentHashRequired: true,
    appShellFallbackForAssets: false
  },
  assets: {
    publicSite: {
      hero: {
        path: FALLBACK_HERO_PATH,
        sha256: "58bbe19d08937a7facd48c81d7344f1c41dbd915d4406c0d010523d40b44663a",
        status: "fallback-stage8-public-hero"
      }
    },
    worldBiomes: {
      skinlands: {
        backdrop: {
          path: FALLBACK_SKINLANDS_GAMEPLAY_PATH,
          sha256: "1e4e99e9f3dced02ef090cdbb21fee213b2724a4c330a1410cf55dff759fd884",
          status: "fallback-stage8-skinlands-gameplay-plate"
        }
      }
    }
  }
};

function requestedQuality(): VisualQuality {
  const urlQuality = new URLSearchParams(window.location.search).get("quality") as VisualQuality | null;
  if (urlQuality && urlQuality in MANIFEST_PATHS) return urlQuality;
  return "stage8";
}

export async function loadStage8VisualManifest(): Promise<{ manifest: Stage8VisualManifest; loaded: boolean; error: string }> {
  const quality = requestedQuality();
  try {
    const response = await fetch(MANIFEST_PATHS[quality], { cache: "no-store" });
    if (!response.ok) throw new Error(`visual manifest ${quality} returned ${response.status}`);
    const manifest = await response.json() as Stage8VisualManifest;
    return { manifest, loaded: true, error: "" };
  } catch (error) {
    return {
      manifest: FALLBACK_MANIFEST,
      loaded: false,
      error: error instanceof Error ? error.message : "visual manifest unavailable"
    };
  }
}

export function stage8HeroPath(manifest: Stage8VisualManifest) {
  return manifest.assets.publicSite?.hero?.path ?? FALLBACK_HERO_PATH;
}

export function stage8SkinlandsGameplayPath(manifest: Stage8VisualManifest) {
  return manifest.assets.worldBiomes.skinlands.backdrop.path;
}

function runtimeGlbPath(manifest: Stage8VisualManifest, key: keyof Stage8RuntimeGlbs) {
  const runtimeGlbs = manifest.assets.runtimeGlbs as Record<string, { path?: string } | string> | undefined;
  const entry = runtimeGlbs?.[key];
  return typeof entry === "string" ? entry : entry?.path;
}

export function stage8RuntimeGlbs(manifest: Stage8VisualManifest): Stage8RuntimeGlbs {
  return {
    ashBoundSmith: runtimeGlbPath(manifest, "ashBoundSmith"),
    weaponGreatsword: runtimeGlbPath(manifest, "weaponGreatsword"),
    remnantChassis: runtimeGlbPath(manifest, "remnantChassis"),
    remnantHead: runtimeGlbPath(manifest, "remnantHead"),
    remnantLimb: runtimeGlbPath(manifest, "remnantLimb")
  };
}

export function stage8RuntimeVisuals(manifest: Stage8VisualManifest): Stage8RuntimeVisuals {
  const ashBoundSmith = manifest.assets.ashBoundSmith as { gameplayCutout?: { path?: string } } | undefined;
  return {
    ashBoundSmithCutout: ashBoundSmith?.gameplayCutout?.path
  };
}

export function applyVisualManifestToDocument(manifest: Stage8VisualManifest) {
  document.documentElement.style.setProperty("--soulsmith-hero-image", `url("${stage8HeroPath(manifest)}")`);
}

export function describeVisualManifest(manifest: Stage8VisualManifest, loaded: boolean, error: string): VisualManifestState {
  return {
    loaded,
    quality: manifest.quality,
    visualVersion: manifest.visualVersion,
    heroPath: stage8HeroPath(manifest),
    skinlandsGameplayPath: stage8SkinlandsGameplayPath(manifest),
    runtimeGlbCount: Object.values(stage8RuntimeGlbs(manifest)).filter(Boolean).length,
    contentHashRequired: Boolean(manifest.delivery?.contentHashRequired),
    appShellFallbackForAssets: Boolean(manifest.delivery?.appShellFallbackForAssets),
    error
  };
}
